import React, { useState } from "react";
import {
  act,
  fireEvent,
  render as rtlRender,
  screen,
  waitFor,
} from "@testing-library/react";
import "@testing-library/jest-dom";
import { CssVarsProvider } from "@mui/joy/styles";
import { theme, resolveBrandScheme } from "../../../../theme/themes";
import { CanvasRenderer } from "./CanvasRenderer";
import { CanvasRuntimeProvider } from "./CanvasRuntimeContext";
const mockAuthFetch = jest.fn();
const mockViewQuery = jest.fn();
function render(ui: React.ReactElement) {
  return rtlRender(ui, {
    wrapper: ({ children }) => (
      <CssVarsProvider theme={theme}>{children}</CssVarsProvider>
    ),
  });
}
jest.mock("../../../../utils/useAuthFetch", () => ({
  useAuthFetch: () => mockAuthFetch,
}));
jest.mock("../../hooks/useViewDataQuery", () => ({
  useViewQuery: () => mockViewQuery(),
}));
const reference = {
  filename: "resume.txt",
  size_bytes: 7,
  path: "stored/resume.txt",
};
let stored: unknown;
function Form({
  editable = true,
  rowId = "7",
  canEdit = editable,
  fieldPath = "resume",
  selectionRow,
  onRefresh,
}: {
  editable?: boolean;
  rowId?: string | null;
  canEdit?: boolean;
  fieldPath?: string;
  selectionRow?: string;
  onRefresh?: () => void;
}) {
  const [value, setValue] = useState(stored);
  return (
    <CanvasRuntimeProvider
      value={
        {
          workspaceId: "ws-1",
          onRefresh,
          canEdit,
          canvasId: "child",
          rowId,
          rowData: {},
          selectionDetail: selectionRow
            ? { rowId: selectionRow, loading: false, error: null }
            : undefined,
          dataSources: [{ name: "main", view_id: "view-1" }],
          embeddingPath: [{ canvasId: "host", elementId: "embedded" }],
        } as any
      }
    >
      <CanvasRenderer
        elements={[
          {
            id: "resume-field",
            type: "field",
            variant: "file",
            props: { label: "CV / Resume" },
            data_binding: { data_source_id: "main", field_path: fieldPath },
            gridColumn: 1,
            gridRow: 1,
            columnSpan: 6,
            rowSpan: 1,
          },
        ]}
        config={{ columns: 12, rowHeight: 60, gap: 8, minRows: 1 }}
        mode="runtime"
        fieldState={{
          [fieldPath]: { value, onChange: editable ? setValue : undefined },
        }}
      />
    </CanvasRuntimeProvider>
  );
}
beforeEach(() => {
  stored = null;
  mockViewQuery
    .mockReset()
    .mockReturnValue({ data: { focus_table_id: "table-1" } });
  mockAuthFetch.mockReset();
  mockAuthFetch.mockImplementation(async (_url, tag) => {
    if (tag === "upload file") {
      stored = reference;
      return reference;
    }
    return {};
  });
});
it("uploads through the scoped file endpoint and displays the persisted reference after reload", async () => {
  const { unmount } = render(<Form />);
  fireEvent.change(await screen.findByLabelText("Choose file to upload"), {
    target: { files: [new File(["content"], "resume.txt")] },
  });
  expect(await screen.findByText("resume.txt")).toBeInTheDocument();
  expect(stored).toEqual(reference);
  expect(mockAuthFetch).toHaveBeenCalledWith(
    "/api/workspace/myvault/files/table-1/7/resume?view_id=view-1&canvas_id=child&element_id=resume-field&embedding_path=host%3Aembedded",
    "upload file",
    expect.objectContaining({
      method: "POST",
      body: expect.any(FormData),
      headers: { "x-workspace-id": "ws-1" },
    }),
  );
  unmount();
  render(<Form editable={false} />);
  expect(await screen.findByText("resume.txt")).toBeInTheDocument();
  expect(
    screen.queryByLabelText("Choose file to upload"),
  ).not.toBeInTheDocument();
  fireEvent.click(screen.getByTitle("Download"));
  await waitFor(() =>
    expect(mockAuthFetch).toHaveBeenCalledWith(
      "/api/workspace/myvault/files/table-1/7/resume?redirect=false&view_id=view-1",
      "download file",
      { headers: { "x-workspace-id": "ws-1" } },
    ),
  );
});
it("removes a stored canvas file with embedding context and refreshes from the server", async () => {
  stored = reference;
  const onRefresh = jest.fn();
  render(<Form onRefresh={onRefresh} />);
  fireEvent.click(screen.getByTitle("Remove file"));
  fireEvent.click(screen.getByRole("button", { name: "Remove" }));
  await waitFor(() =>
    expect(mockAuthFetch).toHaveBeenCalledWith(
      "/api/workspace/myvault/files/table-1/7/resume?view_id=view-1&canvas_id=child&element_id=resume-field&embedding_path=host%3Aembedded",
      "remove file",
      expect.objectContaining({ method: "DELETE" }),
    ),
  );
  await waitFor(() => expect(onRefresh).toHaveBeenCalledTimes(1));
  expect(screen.queryByText("resume.txt")).not.toBeInTheDocument();
});
it("offers no upload without a stored row identity", () => {
  render(<Form rowId={null} />);
  expect(screen.queryByRole("textbox")).not.toBeInTheDocument();
  expect(
    screen.queryByLabelText("Choose file to upload"),
  ).not.toBeInTheDocument();
  expect(screen.getByText("Save row first")).toBeInTheDocument();
});
it("offers no upload to a user without edit", () => {
  render(<Form editable={false} />);
  expect(
    screen.queryByLabelText("Choose file to upload"),
  ).not.toBeInTheDocument();
  expect(screen.getByText("No file")).toBeInTheDocument();
});

it("does not grant upload when embedding/child capabilities deny edit", () => {
  render(<Form editable canEdit={false} />);
  expect(
    screen.queryByLabelText("Choose file to upload"),
  ).not.toBeInTheDocument();
  expect(screen.getByText("No file")).toBeInTheDocument();
});

it("uses selected-card row identity and the plain file column on download", async () => {
  stored = reference;
  render(
    <Form
      editable={false}
      fieldPath="selection-card:resume"
      selectionRow="selected-9"
    />,
  );
  fireEvent.click(await screen.findByTitle("Download"));
  await waitFor(() =>
    expect(mockAuthFetch).toHaveBeenCalledWith(
      "/api/workspace/myvault/files/table-1/selected-9/resume?redirect=false&view_id=view-1",
      "download file",
      { headers: { "x-workspace-id": "ws-1" } },
    ),
  );
});

it.each([false, true])(
  "does not apply a pending selected-row upload after context change (return to original: %s)",
  async (returnToOriginal) => {
    let finish!: (value: unknown) => void;
    mockAuthFetch.mockImplementation((_url, tag) =>
      tag === "upload file"
        ? new Promise((resolve) => {
            finish = resolve;
          })
        : Promise.resolve({}),
    );
    const { rerender } = render(
      <Form fieldPath="selection-card:resume" selectionRow="selected-9" />,
    );
    fireEvent.change(screen.getByLabelText("Choose file to upload"), {
      target: { files: [new File(["content"], "resume.txt")] },
    });
    rerender(
      <Form fieldPath="selection-card:resume" selectionRow="selected-10" />,
    );
    if (returnToOriginal)
      rerender(
        <Form fieldPath="selection-card:resume" selectionRow="selected-9" />,
      );
    await act(async () => {
      stored = reference;
      finish(reference);
    });
    expect(stored).toEqual(reference);
    expect(mockAuthFetch.mock.calls[0][0]).toContain("/selected-9/resume?");
    expect(screen.queryByText("resume.txt")).not.toBeInTheDocument();
    expect(screen.getByLabelText("Choose file to upload")).toBeInTheDocument();
  },
);

it("contains a FILE widget render failure without blanking the surrounding canvas", () => {
  const consoleError = jest
    .spyOn(console, "error")
    .mockImplementation(() => {});
  mockViewQuery.mockImplementation(() => {
    throw new Error("synthetic file widget failure");
  });
  try {
    render(
      <>
        <div>Other canvas content</div>
        <Form />
      </>,
    );
    expect(screen.getByText("Other canvas content")).toBeInTheDocument();
    expect(
      screen.getByText("Unable to display this file field."),
    ).toBeInTheDocument();
  } finally {
    consoleError.mockRestore();
  }
});

it("renders a stored FILE download inside the SSI brand canvas theme", () => {
  stored = reference;
  rtlRender(
    <CssVarsProvider theme={resolveBrandScheme("ssi")}>
      <Form editable={false} />
    </CssVarsProvider>,
  );
  expect(screen.getByText("resume.txt")).toBeInTheDocument();
  expect(screen.getByTitle("Download")).toBeInTheDocument();
  expect(
    screen.queryByLabelText("Choose file to upload"),
  ).not.toBeInTheDocument();
});
